import * as THREE from 'three';
import { View } from '../viewer/View';
import * as OrbitEngine from './orbit/OrbitEngine';
import { isTouchpadNavigation } from '../../modules/Properties/integration/UserSettingsIntegration';

/**
 * Per-canvas orbit controller for widget RenderViews.
 *
 * Lives outside the InputHandler / Viewer / Scene coupling that the main
 * `OrbitControls` is built on. Listens directly to mouse + wheel events on
 * one canvas, manages its own yaw/pitch/radius state via a private `View`,
 * and writes the resulting pose onto a target camera each frame.
 *
 * Math is delegated to `OrbitEngine` so the widget shares the main view's
 * smoothing, zoom-toward-cursor, and tweened zoom-to-location behavior.
 * What stays here: input source (DOM events on one canvas), 2D-mode
 * detection (via `camera.isOrthographicCamera`), and writing the resolved
 * `view` pose onto the camera each tick.
 */
export class WidgetOrbitController {
  private canvas: HTMLCanvasElement;
  /** Target camera (perspective or orthographic). Pose is written here each tick. */
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  /** Private orbit-state for this controller (yaw/pitch/position/radius). */
  readonly view: View;

  /** Engine-owned per-tick deltas. */
  private deltas = OrbitEngine.createDeltas();
  /** Tween list shared with the engine for `zoomToLocation` cleanup. */
  readonly tweens: OrbitEngine.TweenInstance[] = [];

  private rotationSpeed = 5;

  /** Drag state. */
  private dragging: { button: number; lastX: number; lastY: number; moved: boolean } | null = null;
  private lastFrameTime: number;

  /** Whether the controller is enabled. Setting false halts input + decay. */
  enabled = true;

  /**
   * Optional double-click handler. Called with mouse coords in canvas-local
   * pixels (origin: top-left). The widget React component hooks this up to
   * a CPU raycast against the visible point cloud and calls
   * `zoomToLocation()` with the resolved world point.
   */
  onDoubleClick: ((mouseX: number, mouseY: number) => void) | null = null;

  constructor(canvas: HTMLCanvasElement, camera: THREE.PerspectiveCamera | THREE.OrthographicCamera, initialView?: View) {
    this.canvas = canvas;
    this.camera = camera;
    this.view = initialView ?? new View();
    this.lastFrameTime = performance.now();

    this.bind();
  }

  private bind(): void {
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
    this.canvas.addEventListener('dblclick', this.onDblClick);
    // Track mouse on window during drag so the user can drag past the canvas
    // edge without the gesture getting cut off.
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
  }

  dispose(): void {
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('wheel', this.onWheel);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.canvas.removeEventListener('dblclick', this.onDblClick);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    OrbitEngine.stopTweens(this.tweens);
  }

  /** Replace the target camera (e.g. when the view's perspective/ortho mode flips). */
  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    this.camera = camera;
  }

  /**
   * Set the orbit's radius and re-apply to the camera. For an ortho widget
   * the per-frame `applyToCamera` writes `top/bottom = ±radius`, so this is
   * the direct knob for ortho zoom.
   */
  setOrbitRadius(radius: number): void {
    this.view.radius = Math.max(1e-3, radius);
    this.applyToCamera();
  }

  /** Inverse of setOrbitRadius (widget uses `top = radius`, so identity). */
  getOrbitRadius(): number {
    return this.view.radius;
  }

  /**
   * Initialize the view from a camera pose. Sets position, derives yaw/pitch
   * from the look direction, and stores the look distance as radius.
   *
   * `View.lookAt` only sets radius — yaw/pitch must be derived separately
   * via the `direction` setter for the orbit pivot to land on `target`.
   */
  setFromCamera(position: THREE.Vector3, target: THREE.Vector3): void {
    this.view.position.copy(position);
    const toTarget = new THREE.Vector3().subVectors(target, position);
    const radius = toTarget.length();
    if (radius > 1e-6) {
      this.view.direction = toTarget.normalize();
    }
    this.view.radius = Math.max(radius, 1e-3);
    this.applyToCamera();
  }

  // ---- input handlers -------------------------------------------------

  private isOrtho(): boolean {
    return (this.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
  }

  private onContextMenu = (e: MouseEvent): void => {
    // Block right-click menu so the user can use right-drag to pan.
    e.preventDefault();
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (!this.enabled) return;
    // Stop bubble so the main viewport's input handler doesn't also react.
    e.stopPropagation();
    this.canvas.focus?.();
    this.dragging = { button: e.button, lastX: e.clientX, lastY: e.clientY, moved: false };
    OrbitEngine.stopTweens(this.tweens);
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.enabled || !this.dragging) return;
    const dx = e.clientX - this.dragging.lastX;
    const dy = e.clientY - this.dragging.lastY;
    this.dragging.lastX = e.clientX;
    this.dragging.lastY = e.clientY;
    if (Math.abs(dx) + Math.abs(dy) > 0) this.dragging.moved = true;

    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    const ndx = dx / w;
    const ndy = dy / h;

    // Ortho = 2D mode: orbit is blocked, all drags pan (mirrors main view's
    // OrbitControls.getDragMode behavior when in2DMode is set).
    if (this.isOrtho()) {
      OrbitEngine.applyDragPan(this.deltas, ndx, ndy);
    } else if (this.dragging.button === 0) {
      OrbitEngine.applyDragOrbit(this.deltas, ndx, ndy, this.rotationSpeed);
    } else if (this.dragging.button === 2 || this.dragging.button === 1) {
      OrbitEngine.applyDragPan(this.deltas, ndx, ndy);
    }
  };

  private onMouseUp = (_e: MouseEvent): void => {
    this.dragging = null;
  };

  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    e.stopPropagation();

    // Convention (matches main view's InputHandler): wheelDelta = +1 means
    // "zoom in" / "scroll up". Browser deltaY is the opposite (positive =
    // wheel down), so negate.
    const wheelDelta = -Math.sign(e.deltaY);

    if (this.isOrtho()) {
      // 2D mode mirrors the main view: the Navigation-mode user setting
      // decides —
      //   Touchpad (default): plain wheel → pan (vertical scroll moves Y,
      //             horizontal moves X; shift swaps axes), ctrl+wheel
      //             (= trackpad pinch) → zoom toward the cursor.
      //   Mouse: wheel → zoom toward the cursor.
      if (isTouchpadNavigation() && !e.ctrlKey) {
        // applyWheelPan2D expects the same convention `wheelDelta` already
        // holds (= main view's `e.delta`, i.e. -sign(deltaY)). Pass it
        // through unchanged.
        OrbitEngine.applyWheelPan2D(this.deltas, wheelDelta, e.deltaX ?? 0, !!e.shiftKey);
      } else {
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        // Same factor sign as OrbitControls.zoomTowardsMouse: positive
        // factor grows radius (zoom out), negative shrinks (zoom in).
        OrbitEngine.zoomTowardsMouse(this.view, this.camera, new THREE.Vector2(mouseX, mouseY), rect.width, rect.height, -wheelDelta * 0.1, this.deltas);
      }
    } else {
      // 3D: accumulate radiusDelta, smoothing handles the motion.
      OrbitEngine.applyWheelZoom3D(this.deltas, this.view, wheelDelta);
    }
    OrbitEngine.stopTweens(this.tweens);
  };

  private onDblClick = (e: MouseEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = this.canvas.getBoundingClientRect();
    if (this.onDoubleClick) {
      this.onDoubleClick(e.clientX - rect.left, e.clientY - rect.top);
    } else {
      // Fallback when no picker is wired: smooth zoom in. Positive
      // wheelDelta = zoom in (engine convention).
      OrbitEngine.applyWheelZoom3D(this.deltas, this.view, 3);
    }
  };

  // ---- public commands -----------------------------------------------

  /**
   * Programmatic zoom (used by the +/- buttons). `factor > 0` zooms IN,
   * `< 0` zooms out — matching the engine's `applyWheelZoom3D` convention
   * and the main view's wheel direction. Goes through the smoothed delta
   * path so the motion matches a wheel zoom of equivalent magnitude.
   */
  zoom(factor: number): void {
    OrbitEngine.applyWheelZoom3D(this.deltas, this.view, factor * 2);
    OrbitEngine.stopTweens(this.tweens);
  }

  /**
   * Tween toward a world-space target while preserving the camera's
   * current look direction. Caller (the widget React component) supplies
   * the picked location after running its own `pointcloud.pick` against
   * the widget canvas.
   *
   * `targetRadius` defaults to half the current radius (a reasonable
   * "zoom in to the picked point" amount); callers can override.
   */
  zoomToLocation(worldPos: THREE.Vector3, targetRadius?: number): void {
    const minimumJumpDistance = 0.2;
    const view = this.view;
    // Position the camera `targetRadius` from the picked location along
    // the current look direction. Mirror of the main view's math.
    const r = Math.max(minimumJumpDistance, targetRadius ?? view.radius * 0.5);
    const d = view.direction.multiplyScalar(-1);
    const cameraTargetPosition = new THREE.Vector3().addVectors(worldPos, d.multiplyScalar(r));

    OrbitEngine.zoomToLocation({
      view,
      targetPosition: cameraTargetPosition,
      targetRadius: r,
      durationMs: 600,
      onTick: () => this.applyToCamera(),
      tweens: this.tweens,
    });
  }

  // ---- per-frame update ----------------------------------------------

  /**
   * Apply accumulated deltas via the shared engine and write the pose
   * onto the target camera. Call once per frame from the render loop.
   */
  update(): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrameTime) / 1000);
    this.lastFrameTime = now;
    if (!this.enabled) return;

    const ortho = this.isOrtho();
    OrbitEngine.applyDeltas(this.view, this.deltas, this.camera, dt, { allowYaw: !ortho, allowPitch: !ortho });

    this.applyToCamera();
  }

  /**
   * Push the current `view` state onto the target camera. Called every
   * RAF tick from `update()`, and also exposed publicly so external code
   * (RenderViewState) can sync the camera immediately after mutating
   * `view` without waiting for the next animation frame.
   */
  applyViewToCamera(): void {
    this.applyToCamera();
  }

  private applyToCamera(): void {
    const view = this.view;
    this.camera.position.copy(view.position);

    // Construct rotation from yaw/pitch/roll in ZXY Euler order, matching
    // the main viewer's camera writeback in `viewer.update()`. Using
    // `camera.lookAt(pivot)` would derive orientation from a forward+up
    // pair, which becomes degenerate when pitch = ±π/2 (forward parallel
    // to up). The Euler-based path is gimbal-stable at the poles.
    this.camera.rotation.order = 'ZXY';
    this.camera.rotation.x = Math.PI / 2 + view.pitch;
    this.camera.rotation.y = 0;
    this.camera.rotation.z = view.yaw;
    if (view.roll !== 0) {
      const rollQuat = new THREE.Quaternion().setFromAxisAngle(view.direction, view.roll);
      this.camera.quaternion.premultiply(rollQuat);
    }

    // Ortho frustum scales with the orbit radius so wheel/dblclick zoom
    // visibly changes what's on screen. (Perspective doesn't need this —
    // moving the camera position is already what zooming "feels" like.)
    if ((this.camera as THREE.OrthographicCamera).isOrthographicCamera) {
      const ortho = this.camera as THREE.OrthographicCamera;
      // Top/bottom span = radius (full height = 2 * radius). Left/right is
      // re-derived from aspect by the viewer's renderOneView each frame.
      ortho.top = view.radius;
      ortho.bottom = -view.radius;
      ortho.updateProjectionMatrix();
    }

    this.camera.updateMatrixWorld();
    this.camera.matrixWorldInverse.copy(this.camera.matrixWorld).invert();
  }
}
